import { Select, Table, TableColumnsType, TableProps, Typography, Input, InputNumber, Modal, message } from "antd";
import { useEffect, useState } from "react";
import { MonthProduction, Production } from "src/entity/production/production.entity";
import { formatNumber, parseToFixedDecimalWithComma, RoundDecimalToInteger } from "src/utils/process-number";
import { canViewTable } from "../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { excelStore } from "src/stores/excel-store.interface";
import { getProduction, updateProduction } from "src/services/production.service";
import { generateYearArray } from "src/utils/array";
import { productionStore } from "src/stores/production-store";

const { Text } = Typography;
type OnChange = NonNullable<TableProps<any>["onChange"]>;

const ProductionTable_ = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const [data, setData] = useState<Production[]>();
  const { permissions } = useNavigationMenu();
  const [loading, setLoading] = useState(false);
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState<number | null>(currentYear);
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [querySorter, setQuerySorter] = useState("");

  // Production Edit
  const [isOpenConfirmEditModal, setIsOpenConfirmEditModal] = useState<boolean>(false);
  const statusEditProductionModal = productionStore((state) => state.statusEditProductionModal);
  const setStatusEditProductionModal = productionStore((state) => state.setStatusEditProductionModal);

  const excelSetSelectedYear = excelStore(useShallow((state) => state.setSelectedYear));
  const excelSetQueryStr = excelStore(useShallow((state) => state.setQueryStr));

  // Handle input changes for editable table
  const handleInputChange = (recordId: string | number, field: string, value: number | string | null) => {
    if (!data) return;

    const updatedData = data.map((item, index) => {
      // Handle case where record has _id (existing data) or use index (default data)
      const shouldUpdate = item._id ? item._id === recordId : index === recordId;

      if (shouldUpdate) {
        // console.log("Updating record:", { item, field, value, hasId: !!item._id });
        const updatedItem = { ...item, [field]: value } as Production;

        // Recalculate total if it's a month field
        const monthFields = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
        if (monthFields.includes(field)) {
          const total = monthFields.reduce((sum, month) => {
            const monthValue = (updatedItem as any)[month];
            return sum + (typeof monthValue === "number" ? monthValue : 0);
          }, 0);
          updatedItem.total = total;
        }

        return updatedItem;
      }
      return item;
    });

    setData(updatedData);
  };

  const handleSaveProduction = async () => {
    try {
      setLoading(true);
      setStatusEditProductionModal && setStatusEditProductionModal("loading");
      // Call API to save updated production data
      const res = await updateProduction(data, selectedYear || currentYear);
      if (res.data.code === 200) {
        message.success("Production data updated successfully.");
        setStatusEditProductionModal && setStatusEditProductionModal("closed");
      } else {
        message.error("Failed to update production data.");
      }
    } catch (error) {
      message.error("Failed to update production data.");
      console.error("Error updating production data:", error);
    } finally {
      setLoading(false);
      fetchProduction();
      setStatusEditProductionModal && setStatusEditProductionModal("closed");
    }
  };

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
  };

  const createParamFilter = () => {
    let queryParam = "";
    queryParam += `?sort=${querySorter}`;
    if (selectedYear) {
      queryParam += `&year=${selectedYear}`;
    }
    return queryParam;
  };

  const fetchProduction = async () => {
    try {
      if (selectedYear) {
        setLoading(true);
        const query = createParamFilter();
        const response = await getProduction(query);
        if (response.data.code === 200) {
          setData(response.data.data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onTableChange: TableProps<Production>["onChange"] = (_, filter, sorter) => {
    const sorters = Array.isArray(sorter) ? sorter : [sorter];
    if (sorters.length > 0) {
      sorters.forEach((singleSorter) => {
        if (singleSorter && singleSorter.order) {
          const sortOrder = singleSorter.order === "ascend" ? "asc" : "desc";
          setQuerySorter(sortOrder);
        } else {
          setQuerySorter("");
        }
      });
    } else {
      setQuerySorter("");
    }
  };

  useEffect(() => {
    fetchYear();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      excelSetSelectedYear(selectedYear);
      fetchProduction();
    }
  }, [selectedYear]);

  useEffect(() => {
    const queryParam = createParamFilter();
    if (queryParam) {
      excelSetQueryStr(queryParam);
    }
  }, [querySorter, selectedYear]);

  useEffect(() => {
    if (statusEditProductionModal === "open") {
      setStatusEditProductionModal && setStatusEditProductionModal("inprogress");
    } else if (statusEditProductionModal === "confirmed") {
      // เมื่อกดปุ่ม save (edit ครั้งที่ 2) ให้แสดง Modal confirm
      setIsOpenConfirmEditModal(true);
    }
  }, [statusEditProductionModal]);

  const months: MonthProduction[] = [
    { title: "Jan", dataIndex: "jan" },
    { title: "Feb", dataIndex: "feb" },
    { title: "Mar", dataIndex: "mar" },
    { title: "Apr", dataIndex: "apr" },
    { title: "May", dataIndex: "may" },
    { title: "Jun", dataIndex: "jun" },
    { title: "Jul", dataIndex: "jul" },
    { title: "Aug", dataIndex: "aug" },
    { title: "Sep", dataIndex: "sep" },
    { title: "Oct", dataIndex: "oct" },
    { title: "Nov", dataIndex: "nov" },
    { title: "Dec", dataIndex: "dec" },
  ];

  const monthColumns: TableColumnsType<Production> = months.map((month) => ({
    title: month.title,
    width: 100,
    render: (_, record, index) => {
      if (record && typeof record === "object") {
        const value = record[month.dataIndex];

        if (statusEditProductionModal === "open" || statusEditProductionModal === "inprogress") {
          const isEditable = ["SCGC All", "SCGC Subsidiary", "SCGC JV"].includes(record.group || "");

          if (isEditable) {
            // Use _id if available, otherwise use index for default data
            const recordIdentifier = record._id || index;

            return (
              <InputNumber
                value={typeof value === "number" ? value : 0}
                onChange={(newValue) => handleInputChange(recordIdentifier, month.dataIndex, newValue)}
                min={0}
                style={{ width: "120px", minWidth: "120px" }}
                size="small"
                formatter={(value) => (value ? formatNumber(Number(value)) : "0")}
                parser={(value) => (value ? parseFloat(value.replace(/\$\s?|(,*)/g, "")) : 0)}
              />
            );
          } else {
            return <Text>{typeof value === "number" ? RoundDecimalToInteger(value) : 0}</Text>;
          }
        }

        if (typeof value === "number") {
          return value ? <Text>{RoundDecimalToInteger(value)}</Text> : <Text>0</Text>;
        } else {
          return <Text>0</Text>;
        }
      }
    },
  }));

  const staticColumns: TableColumnsType<Production> = [
    {
      title: "No.",
      dataIndex: "no",
      width: 40,
      render: (text: any, record: any, index: number) => {
        return index + 1;
      },
    },
    {
      title: ({ sortColumns }) => {
        const sortedColumn = sortColumns?.find(({ column }) => column.key === "group");
        return (
          <div>
            Company
            {/* <BiSortAlt2 /> */}
            {/* {sortedColumn ? sortedColumn.order === "ascend" ? <BiSortAlt2 /> : <BiSortAlt2 /> : null} */}
          </div>
        );
      },
      showSorterTooltip: false,
      dataIndex: "group",
      key: "company",
      width: 140,
      sorter:
        statusEditProductionModal === "open" || statusEditProductionModal === "inprogress"
          ? false
          : {
              compare: (a, b) => a.group?.localeCompare(b.group || "") || 0,
            },
      render: (text) => {
        return <Text>{text}</Text>;
      },
    },
    {
      title: "Unit",
      dataIndex: "unit",
      width: 50,
      render: (text) => <Text>{text}</Text>,
    },
  ];

  const totalColumns: TableColumnsType<Production> = [
    {
      title: "Total",
      dataIndex: "total",
      width: 100,
      render: (text) => (typeof text === "number" ? <Text>{RoundDecimalToInteger(text)}</Text> : <Text>0</Text>),
    },
  ];

  const columns: TableColumnsType<Production> = [...staticColumns, ...monthColumns, ...totalColumns];
  return (
    <div>
      <div style={{ paddingBottom: "10px" }}>
        <Select
          value={selectedYear}
          style={{ width: 120 }}
          onChange={handleYearChange}
          options={optionYears.map((year) => ({ label: year, value: year }))}
          loading={loading}
          size="middle"
          disabled={statusEditProductionModal === "open" || statusEditProductionModal === "inprogress"}
        />
      </div>

      <Table
        className="w-full"
        style={{ whiteSpace: "nowrap" }}
        dataSource={canViewTable(permissions) ? data : []}
        columns={columns}
        onChange={statusEditProductionModal === "open" || statusEditProductionModal === "inprogress" ? undefined : onTableChange}
        rowKey={(record, index) => record._id || `row-${index}`}
        scroll={{ x: "max-content" }}
        loading={loading}
        pagination={false}
      />

      <Modal
        title="Do you want to save your changes?"
        open={isOpenConfirmEditModal}
        onOk={() => {
          setIsOpenConfirmEditModal(false);
          handleSaveProduction();
        }}
        onCancel={() => {
          setIsOpenConfirmEditModal(false);
          setStatusEditProductionModal && setStatusEditProductionModal("closed");
          fetchProduction();
        }}
        okText="Save"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ProductionTable_;
